package com.zi.support.shared

/**
 * More > Locations: the GPS breadcrumbs each app saves to gps-locations/{app}/{company}/{deviceId}/points (GpsTracker
 * in each app). [PAGE] is the whole screen, a MapLibre map page (OpenFreeMap vector tiles) shared by the web (iframe), Android (WebView) and iOS
 * (WKWebView). The page asks its host for data; the host answers from Firestore:
 *  - page → host: {"id": 1, "type": "devices"} or {"id": 2, "type": "points", "path": "<device path>", "from": ms, "to": ms}
 *    through webkit.messageHandlers.locations (iOS), LocationsHost.post (Android) or parent.postMessage({locations}) (web).
 *  - host → page: locationsReply(id, rows, error), each row a document's fields plus "path", timestamps as epoch ms.
 *  - host → page: setTheme("dark" | "light").
 *  - page → host: {"id": 3, "type": "call", "path", "deviceId", "name"} for "Make a call" on a Support App device (DeviceCalls);
 *    the host opens its call screen and replies locationsReply(id, []).
 */
object GpsLocations {
    val apps = listOf("support-app", "driver-app", "operations-app", "heating-app")

    // Apps can't list Firestore collections, so every company folder an app can write to is listed: Driver Hub's tenant
    // ids, and the companyForServer table Operations and Heating use. ponytail: add a company here when one is added there.
    val companies = listOf("gantrans", "xmoor", "transport", "tyetrans", "avemel", "rungatrans", "kph", "texas", "delta", "zi", "generic", "unassigned")

    /** The collections holding each device's latest fix, one per app and company. */
    val devicePaths: List<String> get() = apps.flatMap { app -> companies.map { "gps-locations/$app/$it" } }

    // No "$" anywhere below: this is a Kotlin string.
    val PAGE = """<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<title>Locations</title>
<link rel="stylesheet" href="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css">
<script src="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js"></script>
<style>
:root{color-scheme:light;--bg:#f2f5f8;--surface:#fff;--subtle:#f5f7fa;--text:#14202b;--muted:#667686;--line:#e2e8ef;--brand:#2e6bf6;--on-brand:#fff;--accent:#2e6bf6;--glass:rgba(255,255,255,.86)}
:root[data-theme="dark"]{color-scheme:dark;--bg:#0e1319;--surface:#151c24;--subtle:#1c2631;--text:#f1f4f7;--muted:#8f9baa;--line:#293542;--brand:#4a86ff;--on-brand:#fff;--accent:#6b9bff;--glass:rgba(21,28,36,.86)}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]){color-scheme:dark;--bg:#0e1319;--surface:#151c24;--subtle:#1c2631;--text:#f1f4f7;--muted:#8f9baa;--line:#293542;--brand:#4a86ff;--on-brand:#fff;--accent:#6b9bff;--glass:rgba(21,28,36,.86)}}
*{box-sizing:border-box}
html,body{margin:0;height:100%;background:var(--bg);color:var(--text);font:14px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
body{display:flex;flex-direction:column;overflow:hidden}
button{font:inherit;color:inherit;cursor:pointer}
.bar{display:flex;gap:8px;align-items:center;padding:8px 10px;background:var(--surface);border-bottom:1px solid var(--line)}
.seg{display:flex;background:var(--subtle);border:1px solid var(--line);border-radius:10px;padding:3px}
.seg button{border:0;background:none;color:var(--muted);padding:6px 14px;border-radius:8px;font-weight:600}
.seg button.on{background:var(--surface);color:var(--text);box-shadow:0 1px 3px rgba(0,0,0,.14)}
.grow{flex:1}.summary{color:var(--muted);font-size:12px;white-space:nowrap}
.icon-btn{border:1px solid var(--line);background:var(--surface);border-radius:10px;min-width:36px;height:36px;padding:0 9px;font-size:16px;font-weight:700}
.icon-btn.on{background:var(--brand);border-color:var(--brand);color:var(--on-brand)}
.chips{display:flex;gap:6px;overflow-x:auto;padding:8px 12px;background:var(--surface);border-bottom:1px solid var(--line);scrollbar-width:none}
.chip{white-space:nowrap;border:1px solid var(--line);background:var(--surface);border-radius:999px;padding:4px 12px 4px 5px;display:flex;align-items:center;gap:7px;font-weight:600;font-size:13px}
.chip.on{background:var(--brand);border-color:var(--brand);color:var(--on-brand)}
.chip .ci{width:26px;height:26px;border-radius:50%;background:var(--subtle);display:grid;place-items:center;overflow:hidden}
.chip .ci svg{width:16px;height:16px}
.chip.all{padding-left:12px}
/* On the map the filters float over it, so the map gets the whole screen. */
#view-map .chips{position:absolute;top:10px;left:10px;right:56px;z-index:5;background:none;border:0;padding:0 0 4px}
#view-map .chip{background:var(--glass);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);box-shadow:0 4px 14px rgba(0,0,0,.14)}
#view-map .chip.on{background:var(--brand)}
.dot{width:10px;height:10px;border-radius:50%;flex:none;box-shadow:0 0 0 2px var(--surface)}
.view{flex:1;min-height:0;display:none;flex-direction:column;position:relative}
.view.on{display:flex}
.map{flex:1;min-height:0}
.maplibregl-map{background:var(--subtle);font:inherit}
.mk{border:2px solid #fff;border-radius:50%;padding:0;box-shadow:0 1px 4px rgba(0,0,0,.4);cursor:pointer}
/* A device on the map: a pin coloured by when it last used the app (green within the hour, orange within two, yellow
   older), with its app's icon in white. MapLibre positions the marker (.pin) with position and transform, so nothing
   here may set either on .pin itself — hover and selection move the inner .pin-in. */
.pin{width:40px;height:50px;padding:0;border:0;background:none;cursor:pointer}
.pin.sel{z-index:3}
.pin-in{display:block;position:relative;width:40px;height:50px;transition:transform .16s ease;transform-origin:50% 100%}
.pin:hover .pin-in,.pin.sel .pin-in{transform:scale(1.15)}
.pin-head{position:absolute;z-index:1;top:0;left:0;width:40px;height:40px;border-radius:50%;background:var(--f);border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.28);display:grid;place-items:center;color:#fff}
.pin-head svg{width:20px;height:20px}
.pin-tip{position:absolute;left:50%;top:31px;width:14px;height:14px;margin-left:-7px;background:var(--f);border-right:3px solid #fff;border-bottom:3px solid #fff;border-radius:0 0 3px 0;transform:rotate(45deg);box-shadow:3px 3px 6px rgba(0,0,0,.18)}
.pin .name{position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:4px;padding:2px 8px;border-radius:999px;background:var(--surface);color:var(--text);font-size:11.5px;font-weight:700;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.2);opacity:0;pointer-events:none;transition:opacity .2s}
.labels .pin .name,.pin:hover .name,.pin.sel .name{opacity:1}
.legend{position:absolute;left:10px;bottom:30px;z-index:5;display:flex;flex-wrap:wrap;gap:4px 12px;padding:7px 12px;border-radius:12px;background:var(--glass);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);box-shadow:0 4px 14px rgba(0,0,0,.14);font-size:12px;font-weight:600}
.legend span{display:inline-flex;align-items:center;gap:6px}.legend i{width:10px;height:10px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.12)}
.app-ic{display:grid;place-items:center;color:var(--c)}.app-ic svg{width:60%;height:60%}
.fig{width:30px;height:30px;flex:none;display:grid;place-items:center;color:var(--c)}.fig svg{width:22px;height:22px}
/* Details popup: a header tinted in the app's colour, quick facts, a details list, Call, then Route. */
.sheet{position:absolute;left:10px;right:10px;bottom:10px;z-index:1000;max-height:78%;overflow:auto;background:var(--surface);border:1px solid var(--line);border-radius:22px;box-shadow:0 22px 60px rgba(0,0,0,.32);padding:0;animation:rise .22s cubic-bezier(.2,.8,.2,1)}
@keyframes rise{from{transform:translateY(16px);opacity:0}}
@media (min-width:720px){.sheet{left:auto;width:400px;top:10px;bottom:auto;max-height:calc(100% - 20px)}}
.sh-head{display:flex;gap:14px;align-items:center;padding:18px 56px 16px 16px;background:linear-gradient(135deg,color-mix(in srgb,var(--c) 22%,var(--surface)),var(--surface) 75%);border-bottom:1px solid var(--line)}
.sh-badge{width:64px;height:64px;border-radius:18px;display:grid;place-items:center;flex:none;background:var(--surface);box-shadow:0 6px 18px color-mix(in srgb,var(--c) 30%,transparent),inset 0 0 0 1px color-mix(in srgb,var(--c) 28%,transparent)}
.sh-badge svg{width:34px;height:34px;color:var(--c)}
.sh-title{margin:0;font-size:18px;font-weight:800;line-height:1.2;letter-spacing:-.01em}
.sh-sub{margin:2px 0 0;color:var(--muted);font-size:13px}
.pill{display:inline-flex;align-items:center;gap:6px;margin-top:8px;padding:3px 10px;border-radius:999px;font-size:12px;font-weight:700}
.pill i{width:7px;height:7px;border-radius:50%;background:currentColor}
.pill.green{background:rgba(22,163,74,.14);color:#16a34a}.pill.orange{background:rgba(234,88,12,.14);color:#ea580c}.pill.yellow{background:rgba(202,138,4,.16);color:#ca8a04}
.age{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px;vertical-align:middle}
.age.green{background:#16a34a}.age.orange{background:#ea580c}.age.yellow{background:#ca8a04}
.sh-body{padding:14px 16px 18px;display:grid;gap:14px}
.facts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.fact{min-width:0;padding:10px;border-radius:14px;background:var(--subtle);text-align:center}
.fact b{display:block;font-size:15px;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fact span{display:block;margin-top:1px;color:var(--muted);font-size:11.5px;font-weight:600}
.info{border:1px solid var(--line);border-radius:16px;overflow:hidden}
.info div{display:flex;justify-content:space-between;gap:14px;padding:11px 14px;border-top:1px solid var(--line)}
.info div:first-child{border-top:0}
.info span{color:var(--muted);flex:none}
.info b{font-weight:600;text-align:right;min-width:0;overflow-wrap:anywhere}
.info a{color:var(--accent);font-weight:600;text-decoration:none}
.mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px}
.tiles{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.tile{min-width:0;background:var(--subtle);border:1px solid var(--line);border-radius:12px;padding:9px 11px}
.tile.wide{grid-column:1/-1}
.tile span{display:block;font-size:11px;font-weight:700;color:var(--muted)}
.tile b{display:block;margin-top:2px;font-size:14px;font-weight:600;word-break:break-word}
.call{display:flex;align-items:center;gap:12px;width:100%;border:0;border-radius:16px;padding:12px 14px;background:linear-gradient(135deg,#22c55e,#15803d);color:#fff;text-align:left;box-shadow:0 10px 24px rgba(22,163,74,.3);transition:transform .12s}
.call:active{transform:scale(.98)}
.call .ic{width:42px;height:42px;border-radius:50%;background:rgba(255,255,255,.22);display:grid;place-items:center;flex:none}
.call strong{display:block;font-size:15px}.call small{display:block;font-size:12px;opacity:.88}
.sub{color:var(--muted);margin:0}
.close{position:absolute;top:14px;right:14px;width:34px;height:34px;border-radius:50%;border:0;background:var(--glass);font-size:20px;line-height:34px;padding:0;color:var(--text);box-shadow:0 2px 8px rgba(0,0,0,.12)}
dl{display:grid;grid-template-columns:auto 1fr;gap:5px 14px;margin:0}
dt{color:var(--muted)}dd{margin:0;word-break:break-word}
.route{padding:14px;border-radius:16px;background:var(--subtle);display:grid;gap:10px}
.route-head{display:flex;align-items:center;justify-content:space-between;gap:8px}
.route-head strong{font-size:15px}
.presets{display:flex;gap:6px;flex-wrap:wrap}
.presets button{border:1px solid var(--line);background:var(--surface);border-radius:999px;padding:4px 10px;font-size:12px;font-weight:700;color:var(--muted)}
.presets button.on{border-color:var(--brand);color:var(--brand)}
.when{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.route label{display:grid;gap:3px;color:var(--muted);font-size:12px;font-weight:600;min-width:0}
.route input{width:100%;background:var(--surface)}
input{font:inherit;color:var(--text);background:var(--subtle);border:1px solid var(--line);border-radius:8px;padding:7px 9px;min-width:0}
.primary{border:0;background:var(--brand);color:var(--on-brand);border-radius:12px;padding:12px 14px;font-weight:700}
.secondary{border:1px solid var(--line);background:var(--surface);border-radius:10px;padding:9px 14px;font-weight:600}
.actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
.crumbs{display:flex;gap:2px;align-items:center;flex-wrap:wrap;padding:10px 12px;background:var(--surface);border-bottom:1px solid var(--line)}
.crumbs button{border:0;background:none;color:var(--accent);font-weight:600;padding:2px 4px}
.crumbs span{color:var(--muted)}.crumbs strong{padding:2px 4px}
.list{flex:1;min-height:0;overflow:auto;padding:12px;display:grid;gap:8px;align-content:start}
.row{display:flex;align-items:center;gap:12px;text-align:left;width:100%;border:1px solid var(--line);background:var(--surface);border-radius:14px;padding:10px 14px;min-height:58px}
.row .fig{width:40px;height:40px;border-radius:12px;background:color-mix(in srgb,var(--c) 13%,var(--surface))}.row .fig svg{width:22px;height:22px}
.row.on{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent)}
.row strong{display:block}.row small{color:var(--muted)}
.row .count{margin-left:auto;color:var(--muted);font-weight:600;white-space:nowrap}
.dayhead{display:flex;gap:10px;align-items:center;padding:10px 12px;background:var(--surface);border-bottom:1px solid var(--line)}
/* One device's day: a big map, the records under it (beside it on wide screens). */
.dayview{flex:1;min-height:0;display:flex;flex-direction:column}
.daymap{flex:none;height:76%;min-height:380px;border-bottom:1px solid var(--line)}
.dayview .list{flex:1}
@media (min-width:900px){.dayview{flex-direction:row}.daymap{flex:1;height:auto;border-bottom:0;border-right:1px solid var(--line)}.dayview .list{flex:none;width:340px}}
.empty{color:var(--muted);text-align:center;padding:30px 10px}
</style></head><body>
<div class="bar">
  <div class="seg" role="tablist"><button role="tab" data-view="map" class="on">Map</button><button role="tab" data-view="browse">By device</button></div>
  <span class="grow"></span><span id="summary" class="summary">Loading…</span>
  <button id="tilt" class="icon-btn on" title="3D view" aria-label="3D view" aria-pressed="true">3D</button>
  <button id="refresh" class="icon-btn" title="Refresh" aria-label="Refresh">&#8635;</button>
</div>
<section id="view-map" class="view on"><div id="chips" class="chips"></div><div id="map" class="map"></div>
  <div class="legend" aria-label="Last used"><span><i style="background:#16a34a"></i>Within 1 hour</span><span><i style="background:#ea580c"></i>1–2 hours</span><span><i style="background:#ca8a04"></i>Over 2 hours</span></div><div id="sheet" class="sheet" hidden></div></section>
<section id="view-browse" class="view"><div id="crumbs" class="crumbs"></div><div id="browse" class="view on"></div><div id="sheet2" class="sheet" hidden></div></section>
<script>
var APPS = [
  {id: "support-app", name: "Support App", color: "#2563eb"},
  {id: "driver-app", name: "Driver Hub", color: "#f97316"},
  {id: "operations-app", name: "Operations App", color: "#16a34a"},
  {id: "heating-app", name: "Heating App", color: "#dc2626"}
];
var COMPANIES = {gantrans: "GanTrans", xmoor: "Xmoor", transport: "Transport DBN", tyetrans: "Tyetrans", avemel: "Avemel",
  rungatrans: "Rungatrans", kph: "KPH Logistics", texas: "Texas", delta: "Delta", zi: "Zora Innovations", generic: "Other company",
  unassigned: "Not logged in yet"};
// Each app's icon (Material Icons): Driver Hub a truck, Support App a headset, Heating App a flame, Operations a warehouse.
var ICONS = {
  "driver-app": "M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9 1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
  "support-app": "M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h4v1h-7v2h6c1.66 0 3-1.34 3-3V10c0-4.97-4.03-9-9-9z",
  "heating-app": "M12 12.9l-2.13 2.09c-.56.56-.87 1.29-.87 2.07C9 18.68 10.35 20 12 20s3-1.32 3-2.94c0-.78-.31-1.52-.87-2.07L12 12.9zM16 6l-.44.55C14.38 8.02 12 7.19 12 5.3V2S4 6 4 13c0 2.92 1.56 5.47 3.89 6.86-.56-.79-.89-1.76-.89-2.8 0-1.32.52-2.56 1.47-3.5L12 10.1l3.53 3.47c.95.93 1.47 2.17 1.47 3.5 0 1.02-.31 1.96-.85 2.75 1.89-1.15 3.29-3.06 3.71-5.3.66-3.55-1.07-6.9-3.86-8.52z",
  "operations-app": "M22 21V7L12 3 2 7v14h5v-9h10v9h5zm-11-2H9v2h2v-2zm2-3h-2v2h2v-2zm2 3h-2v2h2v-2z"
};
function figure(app) { return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="' + (ICONS[app] || ICONS["support-app"]) + '"/></svg>'; }
var FRESH_COLOR = {green: "#16a34a", orange: "#ea580c", yellow: "#ca8a04"};
function appOf(id) { for (var i = 0; i < APPS.length; i++) if (APPS[i].id === id) return APPS[i]; return {id: id, name: id, color: "#64748b"}; }
function companyName(id) { return COMPANIES[id] || id; }
function deviceName(d) { return d.userName || ((d.manufacturer || "") + " " + (d.model || "")).trim() || d.deviceId; }

// The host (web, Android or iOS) answers from Firestore; see GpsLocations.kt.
var pending = {}, nextId = 1;
function ask(type, args) {
  return new Promise(function (resolve, reject) {
    var id = nextId++;
    pending[id] = {resolve: resolve, reject: reject};
    var message = JSON.stringify(Object.assign({id: id, type: type}, args || {}));
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.locations) window.webkit.messageHandlers.locations.postMessage(message);
    else if (window.LocationsHost) window.LocationsHost.post(message);
    else window.parent.postMessage({locations: message}, "*");
  });
}
window.locationsReply = function (id, rows, error) {
  var call = pending[id];
  if (!call) return;
  delete pending[id];
  if (error) call.reject(error); else call.resolve(rows || []);
};

function two(n) { return (n < 10 ? "0" : "") + n; }
function clock(ms) { var d = new Date(ms); return two(d.getHours()) + ":" + two(d.getMinutes()); }
function when(ms) { return ms ? new Date(ms).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"}) + " " + clock(ms) : "—"; }
function ago(ms) {
  var m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return m + " min ago";
  var h = Math.round(m / 60);
  return h < 48 ? h + " h ago" : Math.round(h / 24) + " days ago";
}
function dayInput(ms) { var d = new Date(ms); return d.getFullYear() + "-" + two(d.getMonth() + 1) + "-" + two(d.getDate()); }
function timeInput(ms) { return dayInput(ms) + "T" + clock(ms); }
function startOfDay(ms) { var d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); }
function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return {"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"}[c]; }); }
function coords(p) { return p.latitude.toFixed(5) + ", " + p.longitude.toFixed(5); }
function accuracy(p) { return typeof p.accuracy === "number" ? "± " + Math.round(p.accuracy) + " m" : "—"; }
function km(points) {
  var total = 0;
  for (var i = 1; i < points.length; i++) {
    var a = points[i - 1], b = points[i], r = Math.PI / 180;
    var x = Math.sin((b.latitude - a.latitude) * r / 2), y = Math.sin((b.longitude - a.longitude) * r / 2);
    total += 12742 * Math.asin(Math.sqrt(x * x + Math.cos(a.latitude * r) * Math.cos(b.latitude * r) * y * y));
  }
  return total < 10 ? total.toFixed(1) : Math.round(total);
}
// Detail tiles: [label, value, wide?].
function tiles(rows) {
  return '<div class="tiles">' + rows.map(function (r) { return '<div class="tile' + (r[2] ? " wide" : "") + '"><span>' + esc(r[0]) + "</span><b>" + esc(r[1]) + "</b></div>"; }).join("") + "</div>";
}
function initials(name) { return String(name || "?").trim().split(/\s+/).map(function (w) { return w.charAt(0); }).join("").slice(0, 2).toUpperCase(); }
// Same colours as More > Apps (AppInstalls.freshness): seen within the hour green, 1-2 hours orange, longer yellow.
function freshness(ms) { var age = Date.now() - (ms || 0); return age < 3600000 ? "green" : age < 7200000 ? "orange" : "yellow"; }
var FRESH_LABEL = {green: "Active", orange: "Over an hour ago", yellow: "Over 2 hours ago"};
// Popup header: the app's colour badge with initials, a title, a line under it, and how recently the phone checked in.
function sheetHead(d, title, sub) {
  var f = freshness(d.recordedAt);
  return '<div class="sh-head" style="--c:' + appOf(d.app).color + '"><div class="sh-badge">' + figure(d.app) + "</div><div>" +
    '<p class="sh-title">' + esc(title) + '</p><p class="sh-sub">' + esc(sub) + "</p>" +
    '<span class="pill ' + f + '"><i></i>' + FRESH_LABEL[f] + "</span></div></div>";
}
function facts(rows) { return '<div class="facts">' + rows.map(function (r) { return '<div class="fact"><b>' + esc(r[1]) + "</b><span>" + esc(r[0]) + "</span></div>"; }).join("") + "</div>"; }
function info(rows) { return '<div class="info">' + rows.map(function (r) { return "<div><span>" + esc(r[0]) + "</span><b" + (r[2] ? ' class="mono"' : "") + ">" + r[1] + "</b></div>"; }).join("") + "</div>"; }
function mapsLink(p) { return '<a href="https://www.google.com/maps?q=' + p.latitude + "," + p.longitude + '" target="_blank" rel="noopener">' + esc(coords(p)) + " ↗</a>"; }
function deviceDetails(d) {
  return facts([["Last seen", ago(d.recordedAt)], ["Accuracy", accuracy(d)], ["Version", d.appVersion || "—"]]) +
    info([["User", esc(d.userName || "—")], ["Phone", esc(((d.manufacturer || "") + " " + (d.model || "")).trim() || "—")],
      ["Platform", d.platform === "ios" ? "iOS" : "Android"], ["Last location", esc(when(d.recordedAt))], ["Coordinates", mapsLink(d), true], ["Device ID", esc(d.deviceId), true]]);
}
function pointDetails(p, i, total) {
  return facts([["Time", clock(p.recordedAt)], ["Record", (i + 1) + " of " + total], ["Accuracy", accuracy(p)]]) +
    info([["Saved", esc(when(p.savedAt))], ["Coordinates", mapsLink(p), true]]);
}
// Popups close only with their X.
function openSheet(id, html) {
  var sheet = document.getElementById(id);
  sheet.innerHTML = '<button class="close" aria-label="Close">&times;</button>' + html;
  sheet.hidden = false;
  sheet.querySelector(".close").onclick = function () { sheet.hidden = true; };
  return sheet;
}
// Maps: MapLibre with OpenFreeMap's bright "liberty" style in light and dark mode alike (their "dark" style is near-black and
// unreadable on a phone). Dots are DOM markers; the route line is a GeoJSON layer, re-added whenever a style loads.
var maps = [];
function styleUrl() { return "https://tiles.openfreemap.org/styles/liberty"; }
function makeMap(el, center, zoom) {
  var m = new maplibregl.Map({container: el, style: styleUrl(), center: center, zoom: zoom, attributionControl: {compact: true}});
  m.addControl(new maplibregl.NavigationControl({visualizePitch: true}), "top-right");
  // Names under the figures once zoomed in far enough to read them.
  m.on("zoom", function () { m.getContainer().classList.toggle("labels", m.getZoom() >= 12); });
  // isStyleLoaded() stays false until tiles arrive too, so track the style itself.
  m.on("style.load", function () { m.styleReady = true; drawLine(m); });
  maps.push(m);
  return m;
}
function dropMap(m) { maps = maps.filter(function (x) { return x !== m; }); m.remove(); }
function ll(p) { return [p.longitude, p.latitude]; }
function addDot(m, list, p, color, size, title, onClick, faded) {
  var el = document.createElement("button");
  el.className = "mk"; el.title = title; el.setAttribute("aria-label", title);
  el.style.cssText = "width:" + size + "px;height:" + size + "px;background:" + color + ";opacity:" + (faded ? 0.45 : 1);
  el.onclick = function (e) { e.stopPropagation(); onClick(); };
  list.push(new maplibregl.Marker({element: el}).setLngLat(ll(p)).addTo(m));
}
function addPin(m, list, d, onClick) {
  var el = document.createElement("button"), f = freshness(d.recordedAt);
  el.className = "pin";
  el.style.setProperty("--f", FRESH_COLOR[f]);
  el.setAttribute("aria-label", deviceName(d) + ", " + appOf(d.app).name + ", " + FRESH_LABEL[f]);
  el.innerHTML = '<span class="pin-in"><span class="pin-tip"></span><span class="pin-head">' + figure(d.app) + '</span><span class="name">' + esc(deviceName(d)) + "</span></span>";
  el.onclick = function (e) { e.stopPropagation(); document.querySelectorAll(".pin.sel").forEach(function (x) { x.classList.remove("sel"); }); el.classList.add("sel"); onClick(); };
  list.push(new maplibregl.Marker({element: el, anchor: "bottom"}).setLngLat(ll(d)).addTo(m));
}
var tilt = true;
function clearDots(list) { list.forEach(function (x) { x.remove(); }); list.length = 0; }
function drawLine(m) {
  if (!m.styleReady) return;
  var line = m.routeLine || {coords: [], color: "#2563eb"};
  var data = {type: "Feature", properties: {}, geometry: {type: "LineString", coordinates: line.coords}};
  if (m.getSource("route")) m.getSource("route").setData(data);
  else {
    m.addSource("route", {type: "geojson", data: data});
    m.addLayer({id: "route", type: "line", source: "route", layout: {"line-join": "round", "line-cap": "round"}, paint: {"line-width": 4, "line-opacity": 0.85, "line-color": line.color}});
  }
  m.setPaintProperty("route", "line-color", line.color);
}
function fit(m, points, maxZoom) {
  if (!points.length) return;
  var b = new maplibregl.LngLatBounds();
  points.forEach(function (p) { b.extend(ll(p)); });
  // Room for the figures above each spot and the filters floating over the top.
  m.resize();
  m.fitBounds(b, {padding: {top: 110, bottom: 50, left: 50, right: 60}, maxZoom: maxZoom, duration: 0});
}
function drawRoute(m, dots, points, color, onPoint) {
  clearDots(dots);
  m.routeLine = {coords: points.map(ll), color: color};
  drawLine(m);
  points.forEach(function (p, i) {
    var edge = i === 0 || i === points.length - 1;
    addDot(m, dots, p, i === 0 ? "#16a34a" : i === points.length - 1 ? "#dc2626" : color, edge ? 16 : 11, clock(p.recordedAt), function () { onPoint(p, i); });
  });
  fit(m, points, 16);
  if (tilt && points.length) m.easeTo({pitch: 50, duration: 600});
}
window.setTheme = function (theme) { document.documentElement.setAttribute("data-theme", theme); };
// "Make a call": Support App phones only; the host rings the phone in the app (DeviceCalls).
var PHONE_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25c1.1.37 2.3.57 3.6.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/></svg>';
function callButton(d) {
  if (d.app !== "support-app") return "";
  return '<button class="call" id="make-call"><span class="ic">' + PHONE_ICON + '</span><span><strong>Call ' + esc(deviceName(d).split(" ")[0]) +
    "</strong><small>Rings their Support App · voice call</small></span></button>";
}
function bindCall(sheet, d) {
  var b = sheet.querySelector("#make-call");
  if (b) b.onclick = function () { ask("call", {path: d.path, deviceId: d.deviceId, name: deviceName(d)}); };
}

var devices = [], filter = "all", view = "map", loadError = "";
var map = makeMap("map", [25, -29], 4);
var markers = [], routeDots = [];

function load() {
  document.getElementById("summary").textContent = "Loading…";
  ask("devices").then(function (rows) {
    loadError = "";
    devices = rows.filter(function (d) { return typeof d.latitude === "number" && typeof d.longitude === "number"; })
      .sort(function (a, b) { return (b.recordedAt || 0) - (a.recordedAt || 0); });
    drawMap(true);
    drawBrowse();
  }, function (error) { loadError = String(error); document.getElementById("summary").textContent = loadError; drawBrowse(); });
}

// Map: every device's latest location, by app.
function drawMap(fitAll) {
  var chips = [["all", "All apps", null]].concat(APPS.map(function (a) { return [a.id, a.name, a.color]; }));
  document.getElementById("chips").innerHTML = chips.map(function (c) {
    var n = devices.filter(function (d) { return c[0] === "all" || d.app === c[0]; }).length;
    return '<button class="chip' + (c[2] ? "" : " all") + (filter === c[0] ? " on" : "") + '" data-app="' + c[0] + '">' + (c[2] ? '<span class="ci" style="color:' + c[2] + '">' + figure(c[0]) + "</span>" : "") + esc(c[1]) + " · " + n + "</button>";
  }).join("");
  document.querySelectorAll("#chips .chip").forEach(function (b) { b.onclick = function () { filter = b.getAttribute("data-app"); drawMap(true); }; });
  clearDots(markers);
  var shown = devices.filter(function (d) { return filter === "all" || d.app === filter; });
  shown.forEach(function (d) {
    // Faded: nothing new for over an hour.
    addPin(map, markers, d, function () { showDevice(d); });
  });
  if (view === "map") document.getElementById("summary").textContent = shown.length + (shown.length === 1 ? " device" : " devices");
  if (fitAll && shown.length) { fit(map, shown, 14); map.easeTo({pitch: tilt ? 40 : 0, bearing: 0, duration: 600}); }
}

function showDevice(d) {
  var now = Date.now();
  var sheet = openSheet("sheet", sheetHead(d, deviceName(d), appOf(d.app).name + " · " + companyName(d.company)) + '<div class="sh-body">' + deviceDetails(d) + callButton(d) +
    '<div class="route"><div class="route-head"><strong>Route</strong><div class="presets"><button data-preset="today" class="on">Today</button><button data-preset="day">Last 24 h</button><button data-preset="yesterday">Yesterday</button></div></div>' +
    '<div class="when"><label>From<input type="datetime-local" id="route-from" value="' + timeInput(startOfDay(now)) + '"></label>' +
    '<label>To<input type="datetime-local" id="route-to" value="' + timeInput(now) + '"></label></div>' +
    '<button class="primary" id="route-show">Show route</button><p id="route-note" class="sub"></p></div></div>');
  sheet.querySelectorAll("[data-preset]").forEach(function (b) {
    b.onclick = function () {
      var preset = b.getAttribute("data-preset"), today = startOfDay(now);
      var range = preset === "day" ? [now - 86400000, now] : preset === "yesterday" ? [today - 86400000, today - 60000] : [today, now];
      sheet.querySelector("#route-from").value = timeInput(range[0]); sheet.querySelector("#route-to").value = timeInput(range[1]);
      sheet.querySelectorAll("[data-preset]").forEach(function (x) { x.classList.toggle("on", x === b); });
    };
  });
  map.easeTo({center: ll(d), zoom: Math.max(map.getZoom(), 15), pitch: tilt ? 58 : 0, bearing: tilt ? -20 : 0, duration: 900});
  bindCall(sheet, d);
  sheet.querySelector("#route-show").onclick = function () {
    var from = new Date(sheet.querySelector("#route-from").value).getTime(), to = new Date(sheet.querySelector("#route-to").value).getTime() + 60000;
    var note = sheet.querySelector("#route-note");
    if (!(from < to)) { note.textContent = "Choose a From time before the To time."; return; }
    note.textContent = "Loading…";
    ask("points", {path: d.path, from: from, to: to}).then(function (points) {
      note.textContent = points.length ? points.length + " records · " + km(points) + " km · tap a dot for its details" : "No records in that time.";
      drawRoute(map, routeDots, points, appOf(d.app).color, function (p, i) { showPoint("sheet", p, i, points, d, function () { showDevice(d); }); });
    }, function (error) { note.textContent = String(error); });
  };
}

function showPoint(sheetId, p, i, points, d, back) {
  var day = new Date(p.recordedAt).toLocaleDateString(undefined, {weekday: "short", day: "numeric", month: "short"});
  var sheet = openSheet(sheetId, sheetHead(d, deviceName(d), "Location at " + clock(p.recordedAt) + " · " + day) + '<div class="sh-body">' + pointDetails(p, i, points.length) +
    callButton(d) + (back ? '<div class="actions"><button class="secondary" id="point-back">Back to device</button></div>' : "") + "</div>");
  bindCall(sheet, d);
  if (back) sheet.querySelector("#point-back").onclick = back;
}

// By device: app, then company, then device, then that device's records for one day.
var pick = {app: null, company: null, device: null, day: startOfDay(Date.now())}, dayMap = null, dayDots = [];
function drawBrowse() {
  if (dayMap) { dropMap(dayMap); dayMap = null; dayDots = []; }
  document.getElementById("sheet2").hidden = true;
  var crumbs = [["Apps", function () { pick.app = pick.company = pick.device = null; }]];
  if (pick.app) crumbs.push([appOf(pick.app).name, function () { pick.company = pick.device = null; }]);
  if (pick.company) crumbs.push([companyName(pick.company), function () { pick.device = null; }]);
  if (pick.device) crumbs.push([deviceName(pick.device), null]);
  var bar = document.getElementById("crumbs");
  bar.innerHTML = crumbs.map(function (c, i) {
    var last = i === crumbs.length - 1;
    return (i ? "<span>›</span>" : "") + (last ? "<strong>" + esc(c[0]) + "</strong>" : '<button data-i="' + i + '">' + esc(c[0]) + "</button>");
  }).join("");
  bar.querySelectorAll("button").forEach(function (b) { b.onclick = function () { crumbs[+b.getAttribute("data-i")][1](); drawBrowse(); }; });
  var body = document.getElementById("browse");
  if (pick.device) return drawDay(body);
  var rows;
  if (!pick.app) {
    rows = APPS.map(function (a) {
      var n = devices.filter(function (d) { return d.app === a.id; }).length;
      return [a.id, '<span class="fig" style="--c:' + a.color + '">' + figure(a.id) + '</span><span><strong>' + esc(a.name) + "</strong></span>", n + (n === 1 ? " device" : " devices")];
    });
  } else if (!pick.company) {
    var counts = {};
    devices.forEach(function (d) { if (d.app === pick.app) counts[d.company] = (counts[d.company] || 0) + 1; });
    rows = Object.keys(counts).sort(function (a, b) { return companyName(a).localeCompare(companyName(b)); }).map(function (c) {
      return [c, "<span><strong>" + esc(companyName(c)) + "</strong></span>", counts[c] + (counts[c] === 1 ? " device" : " devices")];
    });
  } else {
    rows = devices.filter(function (d) { return d.app === pick.app && d.company === pick.company; }).map(function (d) {
      return [d.path, '<span class="fig" style="--c:' + appOf(d.app).color + '">' + figure(d.app) + "</span><span><strong>" + esc(deviceName(d)) + "</strong><small><span class=\"age " + freshness(d.recordedAt) + "\"></span>" + esc(((d.manufacturer || "") + " " + (d.model || "")).trim() + " · last seen " + ago(d.recordedAt)) + "</small></span>", "›"];
    });
  }
  var empty = loadError || (devices.length ? "No devices here yet." : "No locations saved yet.");
  body.innerHTML = '<div class="list">' + (rows.length ? rows.map(function (r) { return '<button class="row" data-key="' + esc(r[0]) + '">' + r[1] + '<span class="count">' + esc(r[2]) + "</span></button>"; }).join("") : '<p class="empty">' + esc(empty) + "</p>") + "</div>";
  body.querySelectorAll(".row").forEach(function (b) {
    b.onclick = function () {
      var key = b.getAttribute("data-key");
      if (!pick.app) pick.app = key;
      else if (!pick.company) pick.company = key;
      else pick.device = devices.filter(function (d) { return d.path === key; })[0] || null;
      drawBrowse();
    };
  });
  if (view === "browse") document.getElementById("summary").textContent = "";
}

function drawDay(body) {
  var d = pick.device;
  body.innerHTML = '<div class="dayhead"><input type="date" id="day" value="' + dayInput(pick.day) + '"><span class="grow"></span><span id="day-note" class="summary">Loading…</span></div>' +
    '<div class="dayview"><div id="daymap" class="daymap"></div><div id="records" class="list"></div></div>';
  dayMap = makeMap("daymap", ll(d), 13);
  document.getElementById("day").onchange = function () {
    var value = this.value;
    if (!value) return;
    var parts = value.split("-");
    pick.day = new Date(+parts[0], +parts[1] - 1, +parts[2]).getTime();
    drawBrowse();
  };
  var dayEnd = new Date(pick.day); dayEnd.setDate(dayEnd.getDate() + 1);
  ask("points", {path: d.path, from: pick.day, to: dayEnd.getTime()}).then(function (points) {
    if (pick.device !== d) return;
    document.getElementById("day-note").textContent = points.length ? points.length + " records · " + km(points) + " km" : "";
    var list = document.getElementById("records");
    list.innerHTML = points.length ? points.map(function (p, i) {
      return '<button class="row" data-i="' + i + '"><span><strong>' + esc(clock(p.recordedAt)) + "</strong><small>" + esc(coords(p) + " · " + accuracy(p)) + '</small></span><span class="count">' + (i + 1) + "</span></button>";
    }).join("") : '<p class="empty">No records on this day.</p>';
    function select(p, i) {
      list.querySelectorAll(".row").forEach(function (b) { b.classList.toggle("on", +b.getAttribute("data-i") === i); });
      dayMap.jumpTo({center: ll(p), zoom: Math.max(dayMap.getZoom(), 15)});
      showPoint("sheet2", p, i, points, d, null);
    }
    list.querySelectorAll(".row").forEach(function (b) { b.onclick = function () { var i = +b.getAttribute("data-i"); select(points[i], i); }; });
    drawRoute(dayMap, dayDots, points, appOf(d.app).color, select);
  }, function (error) { document.getElementById("day-note").textContent = String(error); });
}

document.querySelectorAll(".seg button").forEach(function (b) {
  b.onclick = function () {
    view = b.getAttribute("data-view");
    document.querySelectorAll(".seg button").forEach(function (x) { x.classList.toggle("on", x === b); });
    document.getElementById("view-map").classList.toggle("on", view === "map");
    document.getElementById("view-browse").classList.toggle("on", view === "browse");
    if (view === "map") { map.resize(); drawMap(false); } else drawBrowse();
  };
});
document.getElementById("refresh").onclick = load;
document.getElementById("tilt").onclick = function () {
  tilt = !tilt;
  this.classList.toggle("on", tilt); this.setAttribute("aria-pressed", String(tilt)); this.textContent = tilt ? "3D" : "2D";
  maps.forEach(function (m) { m.easeTo({pitch: tilt ? 58 : 0, bearing: tilt ? -20 : 0, duration: 700}); });
};
load();
</script></body></html>"""
}
